import React, { useState, useEffect, useCallback } from 'react';

/**
 * MobilePageAdmin
 * ────────────────
 * Edits the singleton settings doc that drives the public SSR /mobile page
 * ("Add DarijaPro to your phone or desktop" guide).
 *
 * Endpoints: GET / PUT /api/admin/mobile  (admin token required)
 * Visibility flag also controls whether "📱 Mobile" appears in the home nav.
 *
 * Wording rule: never use "install", "download", "app" in visible copy. Stick
 * with "add icon" framing — this is intentional UX from the user.
 *
 * Helper components live at MODULE level (outside the parent function) so
 * React doesn't unmount them on every render — keeping input focus stable
 * while typing.
 */

// ─────────────────────────────────────────────────────────────────────────
// Stable helper components (module-level so they keep input focus)
// ─────────────────────────────────────────────────────────────────────────

const fieldClasses = (isDarkMode) => ({
  cardCls: `rounded-xl border p-5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`,
  labelCls: `block text-xs font-semibold uppercase tracking-wider mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`,
  inputCls: `w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`,
  sectionTitleCls: `text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`,
  smallBtnCls: `px-3 py-1.5 rounded-lg text-xs font-semibold ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'}`,
  dangerBtnCls: `px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-500/10 hover:bg-red-500/20 text-red-600 dark:text-red-400 border border-red-500/20`,
});

const Toggle = ({ k, label, hint, value, onChange, isDarkMode }) => (
  <label className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
    <div className="min-w-0">
      <span className="text-sm font-medium">{label}</span>
      {hint && <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{hint}</div>}
    </div>
    <input type="checkbox" checked={!!value} onChange={(e) => onChange(k, e.target.checked)} className="w-4 h-4 flex-shrink-0 ml-3" />
  </label>
);

const TextField = ({ k, label, value, onChange, isDarkMode, type = 'text', placeholder }) => {
  const { labelCls, inputCls } = fieldClasses(isDarkMode);
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <input type={type} className={inputCls} value={value ?? ''} onChange={(e) => onChange(k, e.target.value)} placeholder={placeholder} />
    </div>
  );
};

const TextArea = ({ k, label, value, onChange, isDarkMode, rows = 3, placeholder }) => {
  const { labelCls, inputCls } = fieldClasses(isDarkMode);
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <textarea className={inputCls} rows={rows} value={value ?? ''} onChange={(e) => onChange(k, e.target.value)} placeholder={placeholder} />
    </div>
  );
};

const StringListEditor = ({
  listKey, label, items, isDarkMode,
  onUpdate, onAdd, onAskDelete,
  addLabel = '+ Add', allowHTML = false, multiline = false,
}) => {
  const { labelCls, inputCls, smallBtnCls, dangerBtnCls } = fieldClasses(isDarkMode);
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="space-y-2">
        {(items || []).map((v, i) => (
          <div key={i} className={`flex items-start gap-2 p-2 rounded-lg border ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
            {multiline ? (
              <textarea className={inputCls + ' flex-1'} rows={2} value={v} onChange={(e) => onUpdate(listKey, i, e.target.value)} />
            ) : (
              <input type="text" className={inputCls + ' flex-1'} value={v} onChange={(e) => onUpdate(listKey, i, e.target.value)} />
            )}
            <button type="button" onClick={() => onAskDelete(listKey, i, (v || '').slice(0, 80))} className={dangerBtnCls + ' flex-shrink-0'}>🗑</button>
          </div>
        ))}
        {allowHTML && (
          <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>HTML allowed — &lt;strong&gt;, &lt;span class="kbd"&gt;Add&lt;/span&gt;, &lt;br&gt;</p>
        )}
        <button type="button" onClick={() => onAdd(listKey)} className={smallBtnCls}>{addLabel}</button>
      </div>
    </div>
  );
};

const StepListEditor = ({
  listKey, label, items, isDarkMode,
  onUpdateStep, onAdd, onAskDelete,
}) => {
  const { labelCls, inputCls, smallBtnCls, dangerBtnCls } = fieldClasses(isDarkMode);
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="space-y-3">
        {(items || []).map((s, i) => (
          <div key={i} className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white flex items-center justify-center text-xs font-bold">{i + 1}</span>
              <span className={`flex-1 text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Step {i + 1}</span>
              <button type="button" onClick={() => onAskDelete(listKey, i, (s.text || '').slice(0, 80) || `Step ${i + 1}`)} className={dangerBtnCls}>🗑 Delete step</button>
            </div>
            <div className="space-y-2">
              <div>
                <span className={`text-[10px] font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>Step text</span>
                <textarea className={inputCls} rows={2} value={s.text || ''} onChange={(e) => onUpdateStep(listKey, i, 'text', e.target.value)} placeholder='e.g. Tap the <strong>Share</strong> button.' />
              </div>
              <div>
                <span className={`text-[10px] font-semibold uppercase tracking-wider ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>Hint (optional, smaller text)</span>
                <textarea className={inputCls} rows={2} value={s.hint || ''} onChange={(e) => onUpdateStep(listKey, i, 'hint', e.target.value)} placeholder="Extra clarification shown below the step." />
              </div>
            </div>
          </div>
        ))}
        <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>HTML allowed in both fields — &lt;strong&gt;, &lt;span class="kbd"&gt;Add&lt;/span&gt;, &lt;br&gt;</p>
        <button type="button" onClick={() => onAdd(listKey)} className={smallBtnCls}>+ Add step</button>
      </div>
    </div>
  );
};

const CommaListEditor = ({ listKey, label, hint, value, onChange, isDarkMode }) => {
  const { labelCls, inputCls } = fieldClasses(isDarkMode);
  const display = (value || []).join(', ');
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <input
        type="text"
        className={inputCls}
        value={display}
        onChange={(e) => onChange(listKey, e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
      />
      {hint && <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{hint}</div>}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────

const MobilePageAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const { cardCls, sectionTitleCls } = fieldClasses(isDarkMode);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await fetch(`${API}/admin/mobile`, { headers: { Authorization: `Bearer ${adminToken}` } });
      if (!r.ok) {
        const body = await r.text().catch(() => '');
        throw new Error(`HTTP ${r.status} ${r.statusText}${body ? ` — ${body.slice(0, 200)}` : ''}`);
      }
      setData(await r.json());
    } catch (e) {
      showNotification?.(`Failed to load mobile page settings: ${e.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [API, adminToken, showNotification]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!data) return;
    try {
      setSaving(true);
      const r = await fetch(`${API}/admin/mobile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify(data),
      });
      if (!r.ok) throw new Error(await r.text());
      const fresh = await r.json();
      setData(fresh);
      showNotification?.('Mobile page saved. Reload /mobile to see changes.', 'success');
    } catch (e) {
      showNotification?.(`Save failed: ${e.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  // ─── Stable callbacks (passed to module-level helpers) ──────────────────
  const update = useCallback((key, value) => setData((d) => ({ ...d, [key]: value })), []);
  const updateStringAt = useCallback((listKey, idx, value) => setData((d) => {
    const arr = [...(d[listKey] || [])];
    arr[idx] = value;
    return { ...d, [listKey]: arr };
  }), []);
  const addString = useCallback((listKey) => setData((d) => ({ ...d, [listKey]: [...(d[listKey] || []), ''] })), []);
  const removeAt = useCallback((listKey, idx) => setData((d) => ({ ...d, [listKey]: (d[listKey] || []).filter((_, i) => i !== idx) })), []);
  const updateStep = useCallback((listKey, idx, key, value) => setData((d) => {
    const arr = [...(d[listKey] || [])];
    arr[idx] = { ...arr[idx], [key]: value };
    return { ...d, [listKey]: arr };
  }), []);
  const addStep = useCallback((listKey) => setData((d) => ({ ...d, [listKey]: [...(d[listKey] || []), { text: '', hint: '' }] })), []);

  const askDelete = useCallback((listKey, idx, previewText) => {
    setConfirmDelete({ listKey, idx, preview: previewText });
  }, []);
  const performDelete = () => {
    if (!confirmDelete) return;
    removeAt(confirmDelete.listKey, confirmDelete.idx);
    setConfirmDelete(null);
  };

  if (loading || !data) {
    return <div className={`p-8 text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading mobile page settings…</div>;
  }

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 max-w-5xl">

      {/* Sticky header with Save */}
      <div className="flex items-center justify-between sticky top-0 z-10 py-3 -mx-6 px-6 backdrop-blur" style={{ background: isDarkMode ? 'rgba(17,24,39,0.85)' : 'rgba(255,255,255,0.85)' }}>
        <div>
          <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>📱 Mobile Page</h2>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Edit the "Add DarijaPro to your phone" guide at <code>/mobile</code>. Toggle visibility, edit copy, manage device sections.
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="px-5 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold shadow disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      {/* Visibility & Nav */}
      <div className={cardCls}>
        <h3 className={sectionTitleCls}>Visibility & Navigation</h3>
        <div className="grid md:grid-cols-2 gap-3">
          <Toggle k="is_visible" label="Show on Home page nav" hint="Turn OFF to hide the 📱 Mobile link from the home page." value={data.is_visible} onChange={update} isDarkMode={isDarkMode} />
          <TextField k="nav_label" label="Nav label" placeholder="📱 Mobile" value={data.nav_label} onChange={update} isDarkMode={isDarkMode} />
        </div>
      </div>

      {/* Programmatic Add button */}
      <div className={cardCls}>
        <h3 className={sectionTitleCls}>One-tap "Add" button (programmatic install)</h3>
        <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          When the visitor's browser supports it (Chrome, Edge, Brave, Samsung, Android),
          a button appears at the top of each device panel that triggers the native install dialog with one click.
          Falls back silently to manual instructions on iOS Safari, Mac Safari, and Firefox.
        </p>
        <div className="space-y-3">
          <Toggle k="show_install_button" label="Show the one-tap Add button" hint="OFF = manual instructions only, no programmatic prompt." value={data.show_install_button} onChange={update} isDarkMode={isDarkMode} />
          <TextField k="ios_button_label" label="iPhone button label" value={data.ios_button_label} onChange={update} isDarkMode={isDarkMode} />
          <TextField k="android_button_label" label="Android button label" value={data.android_button_label} onChange={update} isDarkMode={isDarkMode} />
          <TextField k="desktop_button_label" label="Desktop button label" value={data.desktop_button_label} onChange={update} isDarkMode={isDarkMode} />
          <TextArea k="install_button_help" label="Helper text shown next to the button" rows={2} value={data.install_button_help} onChange={update} isDarkMode={isDarkMode} />
        </div>
      </div>

      {/* Hero */}
      <div className={cardCls}>
        <h3 className={sectionTitleCls}>Hero</h3>
        <div className="space-y-3">
          <TextField k="hero_super" label="Super (small label above title)" value={data.hero_super} onChange={update} isDarkMode={isDarkMode} />
          <div className="grid md:grid-cols-2 gap-3">
            <TextField k="hero_title_part1" label="Title — first part" value={data.hero_title_part1} onChange={update} isDarkMode={isDarkMode} />
            <TextField k="hero_title_part2" label="Title — gradient italic part" value={data.hero_title_part2} onChange={update} isDarkMode={isDarkMode} />
          </div>
          <TextArea k="hero_subtitle" label="Subtitle" rows={3} value={data.hero_subtitle} onChange={update} isDarkMode={isDarkMode} />
        </div>
      </div>

      {/* Trust strip */}
      <div className={cardCls}>
        <h3 className={sectionTitleCls}>Trust bullets (under phone mockup)</h3>
        <StringListEditor
          listKey="trust_bullets" label="Bullets" addLabel="+ Add bullet"
          items={data.trust_bullets} isDarkMode={isDarkMode}
          onUpdate={updateStringAt} onAdd={addString} onAskDelete={askDelete}
        />
      </div>

      {/* Section header */}
      <div className={cardCls}>
        <h3 className={sectionTitleCls}>Device picker section header</h3>
        <div className="space-y-3">
          <TextField k="section_label" label="Eyebrow label" value={data.section_label} onChange={update} isDarkMode={isDarkMode} />
          <TextField k="section_title" label="Section title" value={data.section_title} onChange={update} isDarkMode={isDarkMode} />
          <TextField k="section_sub" label="Right-side sub-text" value={data.section_sub} onChange={update} isDarkMode={isDarkMode} />
        </div>
      </div>

      {/* iPhone */}
      <div className={cardCls}>
        <h3 className={sectionTitleCls}>iPhone / iPad section</h3>
        <div className="space-y-3">
          <Toggle k="ios_visible" label="Show iPhone section" value={data.ios_visible} onChange={update} isDarkMode={isDarkMode} />
          <div className="grid md:grid-cols-3 gap-3">
            <TextField k="ios_icon" label="Icon (emoji)" value={data.ios_icon} onChange={update} isDarkMode={isDarkMode} />
            <TextField k="ios_title" label="Card title" value={data.ios_title} onChange={update} isDarkMode={isDarkMode} />
            <TextField k="ios_desc" label="Card description" value={data.ios_desc} onChange={update} isDarkMode={isDarkMode} />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <CommaListEditor listKey="ios_browsers_ok" label="Supported browsers (✓)" hint="Comma-separated. Shown as green chips." value={data.ios_browsers_ok} onChange={update} isDarkMode={isDarkMode} />
            <CommaListEditor listKey="ios_browsers_warn" label="Unsupported / warn (✗)" hint="Comma-separated. Shown as orange chips." value={data.ios_browsers_warn} onChange={update} isDarkMode={isDarkMode} />
          </div>
          <TextField k="ios_panel_label" label="Instruction panel — eyebrow label" value={data.ios_panel_label} onChange={update} isDarkMode={isDarkMode} />
          <TextField k="ios_panel_title" label="Instruction panel — title" value={data.ios_panel_title} onChange={update} isDarkMode={isDarkMode} />
          <StepListEditor
            listKey="ios_steps" label="Steps"
            items={data.ios_steps} isDarkMode={isDarkMode}
            onUpdateStep={updateStep} onAdd={addStep} onAskDelete={askDelete}
          />
        </div>
      </div>

      {/* Android */}
      <div className={cardCls}>
        <h3 className={sectionTitleCls}>Android section</h3>
        <div className="space-y-3">
          <Toggle k="android_visible" label="Show Android section" value={data.android_visible} onChange={update} isDarkMode={isDarkMode} />
          <div className="grid md:grid-cols-3 gap-3">
            <TextField k="android_icon" label="Icon (emoji)" value={data.android_icon} onChange={update} isDarkMode={isDarkMode} />
            <TextField k="android_title" label="Card title" value={data.android_title} onChange={update} isDarkMode={isDarkMode} />
            <TextField k="android_desc" label="Card description" value={data.android_desc} onChange={update} isDarkMode={isDarkMode} />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <CommaListEditor listKey="android_browsers_ok" label="Supported browsers (✓)" hint="Comma-separated." value={data.android_browsers_ok} onChange={update} isDarkMode={isDarkMode} />
            <CommaListEditor listKey="android_browsers_warn" label="Unsupported / warn (✗)" hint="Comma-separated." value={data.android_browsers_warn} onChange={update} isDarkMode={isDarkMode} />
          </div>
          <TextField k="android_panel_label" label="Instruction panel — eyebrow label" value={data.android_panel_label} onChange={update} isDarkMode={isDarkMode} />
          <TextField k="android_panel_title" label="Instruction panel — title" value={data.android_panel_title} onChange={update} isDarkMode={isDarkMode} />
          <StepListEditor
            listKey="android_steps" label="Steps"
            items={data.android_steps} isDarkMode={isDarkMode}
            onUpdateStep={updateStep} onAdd={addStep} onAskDelete={askDelete}
          />
        </div>
      </div>

      {/* Desktop */}
      <div className={cardCls}>
        <h3 className={sectionTitleCls}>Desktop section</h3>
        <div className="space-y-3">
          <Toggle k="desktop_visible" label="Show Desktop section" value={data.desktop_visible} onChange={update} isDarkMode={isDarkMode} />
          <div className="grid md:grid-cols-3 gap-3">
            <TextField k="desktop_icon" label="Icon (emoji)" value={data.desktop_icon} onChange={update} isDarkMode={isDarkMode} />
            <TextField k="desktop_title" label="Card title" value={data.desktop_title} onChange={update} isDarkMode={isDarkMode} />
            <TextField k="desktop_desc" label="Card description" value={data.desktop_desc} onChange={update} isDarkMode={isDarkMode} />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <CommaListEditor listKey="desktop_browsers_ok" label="Supported browsers (✓)" hint="Comma-separated." value={data.desktop_browsers_ok} onChange={update} isDarkMode={isDarkMode} />
            <CommaListEditor listKey="desktop_browsers_warn" label="Unsupported / warn (✗)" hint="Comma-separated." value={data.desktop_browsers_warn} onChange={update} isDarkMode={isDarkMode} />
          </div>
          <TextField k="desktop_panel_label" label="Instruction panel — eyebrow label" value={data.desktop_panel_label} onChange={update} isDarkMode={isDarkMode} />
          <TextField k="desktop_panel_title" label="Instruction panel — title" value={data.desktop_panel_title} onChange={update} isDarkMode={isDarkMode} />
          <StepListEditor
            listKey="desktop_steps" label="Steps"
            items={data.desktop_steps} isDarkMode={isDarkMode}
            onUpdateStep={updateStep} onAdd={addStep} onAskDelete={askDelete}
          />
        </div>
      </div>

      {/* Help / troubleshooting */}
      <div className={cardCls}>
        <h3 className={sectionTitleCls}>Help / troubleshooting block</h3>
        <div className="space-y-3">
          <TextField k="help_super" label="Eyebrow label" value={data.help_super} onChange={update} isDarkMode={isDarkMode} />
          <TextField k="help_title" label="Title" value={data.help_title} onChange={update} isDarkMode={isDarkMode} />
          <StringListEditor
            listKey="help_bullets" label="Bullets" addLabel="+ Add bullet" allowHTML multiline
            items={data.help_bullets} isDarkMode={isDarkMode}
            onUpdate={updateStringAt} onAdd={addString} onAskDelete={askDelete}
          />
        </div>
      </div>

      {/* CTA */}
      <div className={cardCls}>
        <h3 className={sectionTitleCls}>Call to action (bottom black card)</h3>
        <div className="space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <TextField k="cta_title_part1" label="Title — first part" value={data.cta_title_part1} onChange={update} isDarkMode={isDarkMode} />
            <TextField k="cta_title_part2" label="Title — gradient italic part" value={data.cta_title_part2} onChange={update} isDarkMode={isDarkMode} />
          </div>
          <TextArea k="cta_subtitle" label="Subtitle" rows={2} value={data.cta_subtitle} onChange={update} isDarkMode={isDarkMode} />
          <div className="grid md:grid-cols-2 gap-3">
            <TextField k="cta_btn1_label" label="Button 1 label" value={data.cta_btn1_label} onChange={update} isDarkMode={isDarkMode} />
            <TextField k="cta_btn1_href" label="Button 1 link" value={data.cta_btn1_href} onChange={update} isDarkMode={isDarkMode} />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <TextField k="cta_btn2_label" label="Button 2 label" value={data.cta_btn2_label} onChange={update} isDarkMode={isDarkMode} />
            <TextField k="cta_btn2_href" label="Button 2 link" value={data.cta_btn2_href} onChange={update} isDarkMode={isDarkMode} />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={cardCls}>
        <h3 className={sectionTitleCls}>Footer</h3>
        <div className="space-y-3">
          <TextArea k="remove_note" label="Remove-icon note (small text above footer)" rows={2} value={data.remove_note} onChange={update} isDarkMode={isDarkMode} />
          <TextField k="footer_text" label="Footer copyright line" value={data.footer_text} onChange={update} isDarkMode={isDarkMode} />
        </div>
      </div>

      {/* Bottom save button (mirror of sticky one for long forms) */}
      <div className="flex justify-end pt-2">
        <button
          onClick={save}
          disabled={saving}
          className="px-6 py-2.5 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold shadow disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      {/* ─── Delete confirmation modal (matches BlogCategoriesPanel pattern) ─── */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-[3000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setConfirmDelete(null)}
        >
          <div
            className={`relative w-full max-w-md rounded-2xl shadow-2xl border ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-center gap-3 p-5 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
              <div className="w-10 h-10 rounded-full bg-red-500/15 text-red-500 flex items-center justify-center text-xl font-bold">⚠</div>
              <div className="flex-1 min-w-0">
                <h3 className={`text-lg font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Delete this item?</h3>
                <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  This removes it from the form. Click <strong>Save changes</strong> at the top to make it permanent.
                </p>
              </div>
            </div>

            <div className="p-5">
              <div className={`p-4 rounded-lg border text-sm ${isDarkMode ? 'border-gray-700 bg-gray-800/40 text-gray-200' : 'border-gray-200 bg-gray-50 text-gray-800'}`}>
                {confirmDelete.preview ? (
                  <span dangerouslySetInnerHTML={{ __html: confirmDelete.preview + (confirmDelete.preview.length >= 80 ? '…' : '') }} />
                ) : (
                  <em className="text-gray-500">(empty)</em>
                )}
              </div>
            </div>

            <div className={`flex items-center justify-end gap-2 px-5 py-4 border-t ${isDarkMode ? 'border-gray-800 bg-gray-900/60' : 'border-gray-100 bg-gray-50'} rounded-b-2xl`}>
              <button
                onClick={() => setConfirmDelete(null)}
                className={`px-4 py-2 rounded-lg text-sm font-bold ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-900'}`}
              >
                Cancel
              </button>
              <button
                onClick={performDelete}
                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold"
              >
                🗑 Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobilePageAdmin;
